import React, { useState, useEffect, useContext, useCallback } from "react";
import Parse from "parse";
import PropTypes from "prop-types";
import {
  Card,
  CardContent,
  Typography,
  Collapse,
  IconButton,
  Snackbar,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { formatDate, formatFromTo } from "../FormatDate";
import { linkifyMarkdifySanitize } from "../YouTube";
import WindowTitleComponent from "../WindowTitleComponent";
import { SettingsContext } from "../Settings";
import EetlijstComponent from "./EetlijstComponent";
import "./EventCard.css";
import EventCardHeader from "./EventCardHeader";
import EventCardDetails from "./EventCardDetails";
import EventCardActions from "./EventCardActions";
import EventCardRegistrations from "./EventCardRegistrations";

const EventCard = ({
  doc,
  expanded,
  event,
  isRegistered,
  handleRegisterEvent,
  editEvent,
  makePost,
  routeProps,
  user,
  key,
}) => {
  const [registrations, setRegistrations] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [hasQueriedRegistrationsOnce, setHasQueriedRegistrationsOnce] =
    useState(false);
  const [settingsMenu, setSettingsMenu] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [eetlijstEvent, setEetlijstEvent] = useState(null);
  const context = useContext(SettingsContext);

  const canUserEditEvent = useCallback(() => {
    const isAdmin = context.isAdmin;
    return (
      user.id === event.uid ||
      isAdmin ||
      (event.organizers && event.organizers.map((x) => x.id).includes(user.id))
    );
  }, [context.isAdmin, event.organizers, event.uid, user.id]);

  const queryRegistrations = useCallback(async () => {
    if (
      !doc ||
      (!event.openForRegistration &&
        !(doc.has("hasRegistrations") && doc.get("hasRegistrations")))
    )
      return;

    if (Parse.User.current() && canUserEditEvent()) {
      const query = new Parse.Query("Registration");
      query.equalTo("eventId", doc.id);
      query.limit(1000);
      query.ascending("createdAt");
      const results = await query.find();
      const regs = results.map((object) => object.toJSON());
      setRegistrations(regs);

      const subscription = await query.subscribe();
      setSubscription(subscription);

      subscription.on("create", (object) => {
        setRegistrations((prevRegs) => {
          if (!prevRegs.some((reg) => reg.objectId === object.id)) {
            return [...prevRegs, object.toJSON()];
          }
          return prevRegs;
        });
      });

      subscription.on("update", (object) => {
        setRegistrations((prevRegs) =>
          prevRegs.map((reg) =>
            reg.objectId === object.id ? object.toJSON() : reg,
          ),
        );
      });

      subscription.on("delete", (object) => {
        setRegistrations((prevRegs) =>
          prevRegs.filter((reg) => reg.objectId !== object.id),
        );
      });
    } else {
      const params = { eventId: doc.id };
      const nonPersonalData = await Parse.Cloud.run(
        "getNonPersonalDataFromRegistration",
        params,
      );
      setRegistrations(nonPersonalData);
    }
  }, [canUserEditEvent, doc, event.openForRegistration]);

  useEffect(() => {
    if (expanded) {
      setHasQueriedRegistrationsOnce(true);
      queryRegistrations().then();
    }
  }, [expanded, queryRegistrations]);

  useEffect(() => {
    if (expanded && !hasQueriedRegistrationsOnce) {
      setHasQueriedRegistrationsOnce(true);
      queryRegistrations().then();
    }
  }, [expanded, hasQueriedRegistrationsOnce, queryRegistrations]);

  useEffect(() => {
    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, [subscription]);

  const handleExpandClick = () => {
    routeProps.history.push("/events/" + event.id);
  };

  const handleShareClick = () => {
    if (navigator.share) {
      navigator
        .share({
          title: event.title,
          text: "",
          url: "https://app.desda.org/events/" + doc.id,
        })
        .catch();
    } else {
      navigator.clipboard
        .writeText("https://app.desda.org/events/" + doc.id)
        .then(() => {
          setSnackbarOpen(true);
        });
    }
  };

  const handleEetlijstClick = () => {
    setEetlijstEvent(doc);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleInterestedClick = () => {
    handleRegisterEvent(doc);
  };

  const deleteThisEvent = () => {
    if (doc) {
      doc.destroy();
    }
  };

  const settingsMenuOpen = (event) => {
    setSettingsMenu(event.currentTarget);
  };

  const settingsMenuClose = (option) => {
    setSettingsMenu(null);
    switch (option) {
      case "edit":
        editEvent(doc);
        break;
      case "newsletter":
        const pad2 = (n) => (n < 10 ? "0" : "") + n;
        const e = event;
        let url = "https://administration.desda.org/newsletter?";
        url += "locationNL=" + encodeURIComponent(e.location);
        url += "&descriptionNL=" + encodeURIComponent(e.description);
        url +=
          "&startTime=" +
          pad2(e.startDate.getHours()) +
          ":" +
          pad2(e.startDate.getMinutes());
        url +=
          "&endTime=" +
          pad2(e.endDate.getHours()) +
          ":" +
          pad2(e.endDate.getMinutes());
        url +=
          "&startDate=" +
          e.startDate.getFullYear() +
          "-" +
          pad2(e.startDate.getMonth() + 1) +
          "-" +
          pad2(e.startDate.getDate());
        url +=
          "&endDate=" +
          e.endDate.getFullYear() +
          "-" +
          pad2(e.endDate.getMonth() + 1) +
          "-" +
          pad2(e.endDate.getDate());
        url +=
          "&desdapp=" +
          encodeURIComponent("https://app.desda.org/events/" + e.id);
        url += "&nameNL=" + encodeURIComponent(e.title);
        url += "&submitter=" + encodeURIComponent(user.get("display_name"));
        window.open(url, "_blank");
        break;
      case "37":
        makePost(event);
        routeProps.history.push("/de37/");
        break;
      case "delete":
        setDeleting(true);
        break;
      default:
        break;
    }
  };
  const dateString = formatFromTo(event.startDate, event.endDate, new Date());
  const startDate = formatDate(event.startDate, true, true);
  const equalDate = (x, y) =>
    x.getDate() === y.getDate() &&
    x.getMonth() === y.getMonth() &&
    x.getFullYear() === y.getFullYear() &&
    x.getHours() === y.getHours() &&
    x.getMinutes() === y.getMinutes();
  const endDate = !equalDate(event.startDate, event.endDate)
    ? formatDate(event.endDate, true, true, event.startDate)
    : "";
  const deadline = event.registrationDeadline
    ? new Date(event.registrationDeadline.iso)
    : undefined;
  const opening = event.registrationOpening
    ? new Date(event.registrationOpening.iso)
    : undefined;
  const [sanitizedDescription, youtube] = linkifyMarkdifySanitize(
    event.description,
  );

  return (
    <Card key={key} className={"EventCard-card"} elevation={0}>
      <EventCardHeader
        img={event.img}
        title={event.title}
        handleExpandClick={handleExpandClick}
        expanded={expanded}
      />

      <EventCardDetails
        opening={opening}
        dateRange={dateString}
        location={event.location}
        deadline={deadline}
        expanded={expanded}
      />

      <EventCardActions
        handleExpandClick={handleExpandClick}
        event={event}
        expanded={expanded}
        canUserEditEvent={canUserEditEvent}
        handleInterestedClick={handleInterestedClick}
        setSettingsMenu={setSettingsMenu}
        settingsMenuOpen={settingsMenuOpen}
        isRegistered={isRegistered}
        handleShareClick={handleShareClick}
        handleEetlijstClick={handleEetlijstClick}
        deleteThisEvent={deleteThisEvent}
        deleting={deleting}
        setDeleting={setDeleting}
        settingsMenu={settingsMenu}
        settingsMenuClose={settingsMenuClose}
      />

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <WindowTitleComponent title={event.title} />
        <CardContent>
          <Typography
            className="markdown-rendered"
            component="p"
            dangerouslySetInnerHTML={{ __html: sanitizedDescription }}
          />
          {youtube}

          <EventCardRegistrations
            canUserEditEvent={canUserEditEvent}
            event={event}
            registrations={registrations}
            doc={doc}
          />
        </CardContent>
      </Collapse>

      <EetlijstComponent
        event={eetlijstEvent}
        close={() => setEetlijstEvent(null)}
        isCL={context.isCommissielid || context.isAdmin}
      />

      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={handleSnackbarClose}
        message={<span id="message-id">Link gekopieerd naar het klembord</span>}
        action={
          <IconButton
            key="close"
            aria-label="Close"
            color="inherit"
            style={{ padding: "4px" }}
            onClick={handleSnackbarClose}
          >
            <CloseIcon />
          </IconButton>
        }
      />
    </Card>
  );
};

EventCard.propTypes = {
  doc: PropTypes.object.isRequired,
  expanded: PropTypes.bool,
  event: PropTypes.object.isRequired,
  isRegistered: PropTypes.bool.isRequired,
  handleRegisterEvent: PropTypes.func.isRequired,
  editEvent: PropTypes.func.isRequired,
  makePost: PropTypes.func.isRequired,
  routeProps: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
};

export default EventCard;
